import { Box, Stack, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import {
  AutocompleteInput,
  BooleanInput,
  Create,
  CreateProps,
  FormDataConsumer,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useGetList,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { ReleaseSoftwareComponent, SoftwareComponent, SoftwareRelease } from 'types';
import { SoftwareVersionInput } from './SoftwareVersionInput';

/**
 * A `source` record that is actually usable as a clone. `hardware_id` is what both consumers key
 * off — the read-only hardware input and `cloneVersionsByComponentId` below — so a source without
 * it is not a clone source, only a JSON object that happened to parse.
 */
type CloneSource = Partial<SoftwareRelease> & { hardware_id: number };

/**
 * Whether a parsed `source` can be applied as a clone.
 *
 * Accepts: a plain JSON object carrying `hardware_id` as a finite number greater than zero — what
 * `<CloneButton>` serialises, since the API types the column as an integer id.
 *
 * Rejects: arrays (`[]`, `[{…}]`), `null`, JSON scalars (`1`, `"x"`, `true`), and any object whose
 * `hardware_id` is missing, `null`, `0`, negative, or a string — including a numeric one like
 * `"5"`. A rejected source means no lock and no pre-fill, which is a plain empty create form; the
 * alternative, accepting a shape the rest of the file cannot use, is the read-only-and-empty form
 * this whole change exists to prevent.
 */
const isCloneSource = (value: unknown): value is CloneSource => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

  const { hardware_id: hardwareId } = value as { hardware_id?: unknown };

  return typeof hardwareId === 'number' && Number.isFinite(hardwareId) && hardwareId > 0;
};

/**
 * The release a clone was started from, read back out of the `source` query parameter that
 * react-admin's `<CloneButton>` serialises the list record into (`CloneRelease` in
 * `SoftwareReleaseList`, minus `name`).
 *
 * react-admin parses the very same parameter itself — `useRecordFromLocation` — and resets the
 * form with it, which is what puts `hardware_id` and `custom` in form state. This hook exists only
 * because the per-component versions do not live in a form field: they sit in the record's
 * `software_components`, and each version input has to be handed its own default.
 *
 * Returning a value therefore guarantees one thing to every caller: a record react-admin will have
 * reset the form with, carrying the `hardware_id` the lock and the version map both depend on.
 * Anything else — a parameter that is not parseable JSON, an array, a scalar, an object without a
 * usable `hardware_id` — yields `undefined` rather than throwing: react-admin itself only logs in
 * those cases, and a create form that crashes on a bad URL is worse than one that pre-fills
 * nothing.
 */
const useCloneSource = (): CloneSource | undefined => {
  const [searchParams] = useSearchParams();
  // `getAll`, not `get`: on a hand-built `?source=a&source=b`, react-admin's
  // `getRecordFromLocation` sees an array, logs, and returns null — so the form is never reset —
  // while `get` would hand back the first value and lock the hardware field on an unfilled form.
  const sources = searchParams.getAll('source');
  const source = sources.length === 1 ? sources[0] : undefined;

  return useMemo(() => {
    if (!source) return undefined;

    try {
      const parsed: unknown = JSON.parse(source);
      return isCloneSource(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }, [source]);
};

/**
 * The clone source's version selection, keyed by software component id.
 *
 * The API builds a release's `software_components` from its *versions*
 * (`SoftwareReleaseResource::formatUpdateChannels()` in toverzicht-api): each entry is the raw
 * software component model as eager-loaded by `SoftwareReleaseController::$with`
 * (`softwareComponent:id,name` with `hardwareRevisions:id`), plus `software_version_id`. So an
 * entry carries `id`, `name`, `hardware_revisions` and `software_version_id` — and **no**
 * `hardware_ids`, which is added by `SoftwareComponentResource` and therefore only exists on
 * records that came from the `software_components` endpoint. Filtering these entries on
 * `hardware_ids` matched nothing and left every dropdown empty (TVR-1745).
 *
 * Component ids are unique, so the id alone identifies the entry without risk of matching another
 * component. Hardware is checked once, against the release as a whole, rather than once per entry:
 * a clone keeps its source's hardware (the input is read-only), and if that ever stops being true
 * the source's versions do not belong to the selected hardware at all.
 *
 * A per-entry check was available and was deliberately not taken. Each entry's `hardware_revisions`
 * *is* the component's compatible hardware list, so the entries could have been filtered
 * individually. The release-level gate is the safer direction under the one condition that would
 * make the two differ — the hardware lock being lifted: it then yields an empty map and pre-fills
 * nothing, whereas a per-entry predicate would pre-fill a version belonging to the *other*
 * hardware, an id the version `ReferenceInput` does not even offer, sitting in a submitted payload.
 *
 * The mismatch early return is consequently unreachable through the UI today — the hardware input
 * is `readOnly` for a clone, so the form's `hardware_id` is the source's. It is deliberate defence
 * for the day that changes, not dead code; keep it. It is also not worth exporting this function to
 * cover that branch in a unit test.
 */
const cloneVersionsByComponentId = (source: CloneSource | undefined, hardwareId: number) => {
  const versions = new Map<number, number>();

  if (!source || hardwareId == null || source.hardware_id !== Number(hardwareId)) {
    return versions;
  }

  source.software_components?.forEach((component: ReleaseSoftwareComponent) => {
    // An entry whose component row is gone arrives with only `software_version_id` — see
    // `ReleaseSoftwareComponent` in `types/`. It matches no component here and is skipped.
    if (component.id != null && component.software_version_id != null) {
      versions.set(component.id, component.software_version_id);
    }
  });

  return versions;
};

const SoftwareComponentInput = () => {
  const id: number = useWatch({ name: 'hardware_id' });
  const cloneSource = useCloneSource();

  const { data, isLoading } = useGetList<SoftwareComponent>('software_components', {
    filter: {
      hardware_id: id,
    },
  });

  const clonedVersions = useMemo(() => cloneVersionsByComponentId(cloneSource, id), [cloneSource, id]);

  // Returning null until the hardware and its components are known is also what makes the defaults
  // below land: `<AutocompleteInput defaultValue>` applies once, when the input mounts, and by then
  // react-admin has already reset the form with the cloned record, so `hardware_id` — and with it
  // each input's choice set — is settled.
  if (!id || isLoading) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack>
        {data?.map((component) => (
          <SoftwareVersionInput
            key={component.id}
            component={component}
            source={component.name}
            defaultValue={clonedVersions.get(component.id)}
          />
        ))}
      </Stack>
    </Box>
  );
};
const SoftwareReleaseCreateToolbar = (props: ToolbarProps) => {
  const id: number = useWatch({ name: 'hardware_id' });
  const { data: components, isLoading } = useGetList<SoftwareComponent>('software_components', {
    filter: {
      hardware_id: id,
    },
  });

  return (
    <Toolbar {...props}>
      <SaveButton
        disabled={!id || isLoading}
        transform={({ tover, ...rest }) => {
          return {
            ...rest,
            software_components: components?.map((component) => {
              return {
                ...component,
                software_version_id: tover[component.name.split('.')[1]],
              };
            }),
          };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const SoftwareReleaseCreate: FC<CreateProps> = () => {
  // Locked means a record was actually applied, not that a `source` parameter was present: the
  // hook only returns for a source react-admin will have reset the form with and that carries a
  // usable `hardware_id`. Locking on mere presence would leave a read-only, empty, unusable form.
  const clonedRelease = useCloneSource() !== undefined;

  return (
    <Create>
      <SimpleForm toolbar={<SoftwareReleaseCreateToolbar />}>
        <Typography variant="h4">Create release</Typography>
        <TextInput source="name" fullWidth validate={required()} />
        <ReferenceInput source="hardware_id" reference="hardware">
          <AutocompleteInput
            optionText="name"
            fullWidth
            readOnly={clonedRelease}
            validate={required()}
            helperText={clonedRelease && 'clone: read only'}
          />
        </ReferenceInput>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData.hardware_id && (
              <ReferenceInput
                source="hardware_revision_id"
                reference="hardware_revisions"
                filter={{ hardware_id: formData.hardware_id }}
              >
                <SelectInput optionText="name" fullWidth validate={required()} />
              </ReferenceInput>
            )
          }
        </FormDataConsumer>
        <BooleanInput source="custom" />
        <Typography variant="h5">Software Components</Typography>
        <SoftwareComponentInput />
      </SimpleForm>
    </Create>
  );
};
